import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Search,
  CheckCircle2,
  Sparkles,
  ShoppingCart,
  TrendingUp,
  BarChart3,
  Wallet,
  Layers,
  Brain,
  CircleDot,
  Eye,
} from "lucide-react";
import { useLocale } from "../../i18n/LocaleContext";

/* Pill helper */
const Pill = ({ children, tint = "slate" }) => {
  const map = {
    slate: "bg-slate-100 text-slate-700 border-slate-200",
    blue: "bg-[#0072CE]/10 text-[#0072CE] border-[#0072CE]/20",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    amber: "bg-amber-50 text-amber-800 border-amber-200",
    cyan: "bg-cyan-50 text-cyan-700 border-cyan-200",
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-bold border ${map[tint]}`}>
      {children}
    </span>
  );
};

/* ---------- Mock 1: Catalog + AI Match ---------- */
function CatalogPreview() {
  return (
    <div className="relative bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center gap-2">
        <div className="h-7 w-7 rounded bg-[#0072CE] text-white flex items-center justify-center text-xs font-display font-bold">i</div>
        <div className="text-xs font-display font-bold text-slate-900">UniBuy™</div>
        <div className="flex-1 h-7 ml-3 rounded-md bg-slate-50 border border-slate-200 flex items-center px-2 text-xs text-slate-500">
          <Search size={11} className="mr-1.5" />
          <span className="font-mono">6205-2RS</span>
        </div>
      </div>
      <div className="p-3 grid grid-cols-2 gap-2">
        {/* exact match */}
        <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/40 p-2.5 col-span-2">
          <div className="flex items-center justify-between">
            <Pill tint="emerald"><CheckCircle2 size={10} /> 100% match</Pill>
            <span className="text-[10px] font-mono text-slate-500">IS-10001</span>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <div className="h-12 w-12 rounded bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center">
              <CircleDot size={20} className="text-cyan-300" />
            </div>
            <div className="min-w-0">
              <div className="font-display text-[13px] font-semibold text-slate-900 leading-tight">SKF Deep Groove Ball Bearing</div>
              <div className="mt-0.5 text-[10px] text-slate-500">SKF · 25×52×15 mm · sealed</div>
            </div>
            <div className="ml-auto text-right">
              <div className="text-[10px] text-slate-400 line-through">€18.40</div>
              <div className="font-display text-sm font-bold text-slate-900">€15.50</div>
              <Pill tint="emerald">−15.75%</Pill>
            </div>
          </div>
        </div>
        {/* close match */}
        <div className="rounded-lg border border-slate-200 p-2.5">
          <Pill tint="slate">88% match</Pill>
          <div className="mt-2 text-[11px] font-display font-semibold text-slate-900">NTN 6205LLU</div>
          <div className="text-[10px] text-slate-500">Power Transmission</div>
          <div className="mt-1 font-display text-xs font-bold text-slate-900">€14.79</div>
        </div>
        <div className="rounded-lg border border-slate-200 p-2.5">
          <Pill tint="slate">72% match</Pill>
          <div className="mt-2 text-[11px] font-display font-semibold text-slate-900">FAG 6205-2RSR</div>
          <div className="text-[10px] text-slate-500">Power Transmission</div>
          <div className="mt-1 font-display text-xs font-bold text-slate-900">€16.10</div>
        </div>
      </div>
    </div>
  );
}

/* ---------- Mock 2: Topaz AI Fuzzy Mapping ---------- */
function MappingPreview() {
  return (
    <div className="relative bg-slate-950 rounded-xl border border-slate-800 shadow-sm overflow-hidden text-white">
      <div className="px-4 py-2.5 border-b border-white/10 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain size={13} className="text-cyan-300" />
          <span className="text-xs font-display font-bold">Topaz AI · Distributor → OEM</span>
        </div>
        <Pill tint="cyan">Internal · Procurement</Pill>
      </div>
      <div className="p-3 space-y-2">
        <div className="rounded-lg bg-white/5 border border-white/10 p-2.5">
          <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">Customer Past Purchase</div>
          <div className="mt-1 text-[12px] font-mono">MOTION · MOT-BB-6205-2RS</div>
          <div className="text-[11px] text-slate-300">Deep groove ball bearing 25mm</div>
        </div>
        <div className="flex items-center justify-center text-slate-500">
          <Sparkles size={14} className="text-cyan-300 mr-1.5" />
          <span className="text-[10px] uppercase tracking-[0.18em] font-bold">AI fuzzy match</span>
        </div>
        <div className="rounded-lg bg-emerald-500/10 border border-emerald-500/30 p-2.5">
          <div className="flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">Identified OEM</div>
            <Pill tint="emerald"><CheckCircle2 size={10} /> 100% Crosswalk</Pill>
          </div>
          <div className="mt-1 text-[12px] font-semibold">SKF · 6205-2RS</div>
          <div className="text-[11px] text-emerald-200/80">→ UniBuy™ SKU IS-10001 · €15.50</div>
        </div>
        <div className="grid grid-cols-4 gap-1.5 mt-1">
          {[{ l: "Exact", v: "7" }, { l: "High", v: "1" }, { l: "Medium", v: "2" }, { l: "No Match", v: "1" }].map((s) => (
            <div key={s.l} className="rounded bg-white/5 border border-white/10 px-2 py-1">
              <div className="text-[8px] uppercase tracking-wider text-slate-400 font-bold">{s.l}</div>
              <div className="text-xs font-bold">{s.v}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ---------- Mock 3: Cart Transfer to ERP ---------- */
function CartPreview() {
  return (
    <div className="relative bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <ShoppingCart size={13} className="text-slate-700" />
          <span className="text-xs font-display font-bold text-slate-900">Cart · CART-48217</span>
        </div>
        <span className="text-[10px] font-mono text-slate-500">3 / 6</span>
      </div>
      <div className="p-3 space-y-1.5">
        {[
          { name: "SKF 6205-2RS", qty: 24, price: 15.50 },
          { name: "3M 1860 N95 Pk-20", qty: 5, price: 35.21 },
          { name: "ABB MCB S203 16A", qty: 6, price: 38.85 },
        ].map((l) => (
          <div key={l.name} className="flex items-center justify-between text-xs">
            <div className="min-w-0 truncate text-slate-900 font-medium">{l.name}</div>
            <div className="shrink-0 text-slate-500 font-mono ml-2">×{l.qty} · €{(l.qty * l.price).toFixed(2)}</div>
          </div>
        ))}
      </div>
      <div className="px-3 py-2 border-t border-slate-100 flex items-baseline justify-between">
        <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total (before tax)</span>
        <span className="font-display text-sm font-bold text-slate-900">€779.20</span>
      </div>
      <div className="px-3 pb-3">
        <div className="rounded-md bg-[#0072CE] text-white py-2 text-center text-xs font-semibold inline-flex items-center justify-center w-full gap-1.5">
          Transfer Cart for PO Creation <ArrowUpRight size={12} />
        </div>
        <div className="mt-1.5 text-[10px] text-center text-slate-500">→ SAP Ariba · cXML PunchOut · Order OMS-2511-48217</div>
      </div>
    </div>
  );
}

/* ---------- Mock 4: Spend & Savings Dashboard ---------- */
function DashboardPreview() {
  const years = [
    { y: 2024, s: 850, sv: 76 },
    { y: 2025, s: 1003, sv: 120 },
    { y: 2026, s: 540, sv: 81, run: true },
  ];
  const max = Math.max(...years.map((y) => y.s));
  return (
    <div className="relative bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <BarChart3 size={13} className="text-slate-700" />
          <span className="text-xs font-display font-bold text-slate-900">JDE Peet&apos;s · Org Dashboard</span>
        </div>
        <Pill tint="amber">CPO · CFO view</Pill>
      </div>
      <div className="p-3 grid grid-cols-2 gap-2">
        <div className="col-span-1 rounded-lg border border-slate-200 p-2.5">
          <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">YTD Spend</div>
          <div className="font-display text-lg font-bold text-slate-900">€540k</div>
          <div className="text-[10px] text-slate-500">target €1.2M</div>
        </div>
        <div className="col-span-1 rounded-lg border border-emerald-200 bg-emerald-50/40 p-2.5">
          <div className="text-[9px] uppercase tracking-wider text-emerald-700 font-bold">YTD Savings</div>
          <div className="font-display text-lg font-bold text-emerald-700">€81k</div>
          <div className="text-[10px] text-emerald-700">15% effective</div>
        </div>
      </div>
      <div className="px-3 pb-3">
        <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold mb-2">3-year trend (€k)</div>
        <div className="grid grid-cols-3 gap-3">
          {years.map((y) => (
            <div key={y.y}>
              <div className="flex items-baseline justify-between">
                <span className="text-[10px] text-slate-500 font-bold">{y.y}</span>
                {y.run && <span className="text-[8px] uppercase tracking-wider text-amber-700 font-bold">run-rate</span>}
              </div>
              <div className="font-display text-sm font-bold text-slate-900">€{y.s}k</div>
              <div className="text-[10px] text-emerald-700 font-medium">€{y.sv}k saved</div>
              <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-slate-900" style={{ width: `${(y.s / max) * 100}%` }} />
              </div>
              <div className="mt-1 h-1 rounded-full bg-emerald-50 overflow-hidden">
                <div className="h-full bg-emerald-500" style={{ width: `${(y.sv / max) * 100}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const PILLARS = [
  {
    n: "01",
    eyebrow: "Search & Browse",
    title: "UniBuy™ catalog with 100% AI matching",
    body: "32 indirect categories. Topaz AI scores every search by UniBuy™ PN, brand, description or manufacturer PN — exact matches surface with a 100% badge.",
    accent: "blue",
    preview: <CatalogPreview />,
    link: { to: "/infoshop", label: "Open UniBuy™" },
  },
  {
    n: "02",
    eyebrow: "Internal · Procurement",
    title: "AI fuzzy mapping of incumbent spend to OEMs",
    body: "Upload purchase history from MOTION, RS Components, Sonepar, Rexel, Grainger, Würth or Eriks — Topaz identifies the underlying OEM and the matching UniBuy™ SKU.",
    accent: "cyan",
    preview: <MappingPreview />,
    link: { to: "/infoshop/admin/enrichment?demo=1", label: "Open OMNIMatch AI" },
  },
  {
    n: "03",
    eyebrow: "Procurement Workbench",
    title: "From cart to PO in a single click",
    body: "Buyers shop the UniBuy™ catalog and hand the cart straight back to their ERP — Coupa, SAP Ariba, Aerchain, Jaggaer, ivalua, SAP S/4HANA or Oracle Fusion — over standard PunchOut OCI / cXML. The requisition, approval and PO are generated inside the customer's existing procurement system, so governance, audit trail and AP workflows stay untouched.",
    accent: "blue",
    preview: <CartPreview />,
    link: { to: "/infoshop", label: "See the cart flow" },
  },
  {
    n: "04",
    eyebrow: "Analytics for the C-suite",
    title: "Spend & Savings Dashboard — CPO · CFO · CSO",
    body: "YTD spend, realized savings, top buyers, category mix and three-year YoY trend at both individual and organization scope. Built for board-level reporting.",
    accent: "amber",
    preview: <DashboardPreview />,
    link: { to: "/infoshop/dashboard?network_id=INFOSYS.PROCUREMENT.ANALYST&company_name=INFOSYS%20BPM", label: "Open the dashboard" },
  },
];

export default function PlatformTour() {
  const { t } = useLocale();
  return (
    <section
      id="platform"
      data-testid="platform-tour-section"
      className="bg-white py-24 md:py-32 border-y border-slate-200"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl mb-14">
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em] text-slate-500 font-semibold mb-4">
            <span className="inline-block h-px w-8 bg-slate-300" />
            <span>{t("landing.platform.eyebrow")}</span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl xl:text-[56px] font-semibold tracking-[-0.02em] leading-[1.05] text-slate-900">
            {t("landing.platform.headline_a")} <span className="text-slate-400">{t("landing.platform.headline_b")}</span>
          </h2>
          <p className="mt-4 text-slate-600 text-lg leading-relaxed font-light">
            {t("landing.platform.body")}
          </p>
        </div>

        <div className="space-y-16 md:space-y-24">
          {PILLARS.map((p, i) => {
            const reverse = i % 2 === 1;
            return (
              <div
                key={p.n}
                data-testid={`platform-pillar-${p.n}`}
                className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center ${reverse ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="lg:col-span-5 space-y-4">
                  <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">
                    <span className="font-mono text-[#0072CE]">{p.n}</span>
                    <span className="inline-block h-px w-6 bg-slate-300" />
                    <span>{p.eyebrow}</span>
                  </div>
                  <h3 className="font-display text-2xl md:text-3xl font-semibold tracking-[-0.01em] text-slate-900 leading-tight">
                    {p.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">{p.body}</p>
                  <Link
                    to={p.link.to}
                    data-testid={`pillar-cta-${p.n}`}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0072CE] hover:text-[#005BA6]"
                  >
                    {p.link.label} <ArrowUpRight size={14} />
                  </Link>
                </div>
                <div className="lg:col-span-7">
                  <div className="relative">
                    {/* outer halo */}
                    <div className="absolute -inset-3 rounded-2xl bg-gradient-to-br from-[#0072CE]/10 via-transparent to-cyan-200/20" aria-hidden="true" />
                    <div className="relative">{p.preview}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
